import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import remoteDbService from '@/core/database/RemoteDBService';
import { type DisplayedCollectionSection } from '@/features/collections/types/collection.types';
import { type Page, type PageMetadata } from '@/features/pages/types/page.types';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';

export const useOptimizedCollectionNotes = () => {
  const setLoading = useCollectionStore(state => state.setLoading);
  const currentCollection = useCollectionStore(state => state.currentCollection);

  // Stage 1: Fast metadata query for sidebar structure
  const { data: pagesMetadata, isLoading: isMetadataLoading } = useQuery<PageMetadata[]>({
    queryKey: [`collection-notes-metadata/${currentCollection?.id || ''}`],
    queryFn: async () => {
      if (!currentCollection?.id) throw new Error('Collection ID is required');
      return await remoteDbService.getCollectionNotesMetadata(currentCollection.id);
    },
    enabled: !!currentCollection?.id,
  });

  // Stage 2: Background full content query
  const { data: fullPages, isLoading: isContentLoading } = useQuery<Page[]>({
    queryKey: [`collection-notes-full/${currentCollection?.id || ''}`],
    queryFn: async () => {
      if (!currentCollection?.id) throw new Error('Collection ID is required');
      return await remoteDbService.getCollectionNotes(currentCollection.id);
    },
    enabled: !!currentCollection?.id && !!pagesMetadata, // Only start after metadata is loaded
  });

  // Update loading state based on metadata loading (for quick sidebar)
  useEffect(() => {
    setLoading(isMetadataLoading);
  }, [isMetadataLoading, setLoading]);

  // Build collection structure with metadata first, then enhance with full content
  const structure: DisplayedCollectionSection[] | undefined = useMemo(() => {
    if (!currentCollection) return undefined;

    const sections = currentCollection.sections || [];
    const pagesToUse = fullPages || pagesMetadata; // Use full pages if available, fall back to metadata

    if (!pagesToUse) return undefined;

    return sections
      .filter(s => !s.archivedAt)
      .map(section => ({
        section: {
          ...section,
          pages: sortBy(
            pagesToUse.filter(p => p.sectionId === section.id),
            'orderNumber'
          ),
        },
      }));
  }, [currentCollection, pagesMetadata, fullPages]);

  return {
    // Metadata for quick sidebar rendering
    pagesMetadata,
    // Full pages with content (may be undefined while loading)
    fullPages,
    // Current structure (metadata first, then enhanced with content)
    structure,
    // Loading states
    isMetadataLoading,
    isContentLoading,
    isLoading: isMetadataLoading, // For backward compatibility
    // Status helpers
    hasMetadata: !!pagesMetadata,
    hasFullContent: !!fullPages,
    hasStructure: !!structure,
  };
};
